<template>
  <div class="dashboard-shell pos-dashboard">
    <div class="dashboard-head">
      <div>
        <h2>Business Dashboard</h2>
        <p>Live sales, profit, stock alerts, cash position, receivables and cheques.</p>
      </div>
      <button class="refresh-btn" @click="refreshDashboard" :disabled="loading">
        {{ loading ? "Refreshing..." : "Refresh Dashboard" }}
      </button>
    </div>

    <div class="loading-banner" v-if="loading && !hasData">Loading dashboard data...</div>

    <div class="kpi-grid">
      <MetricCard v-for="card in kpiCards" :key="card.title" :class="card.tone" :card="card" />
    </div>

    <div class="section-label">Business Performance</div>
    <PerformancePanel :summary="performanceSummary" />

    <div class="section-label financial-section-label">Financial Position</div>
    <div class="financial-grid">
      <MetricCard
        v-for="card in financialPositionCards"
        :key="card.title"
        class="small financial-position-card"
        :card="card"
      />
    </div>

    <div class="cash-grid cheque-grid">
      <ChequeRegister :cheques="cheques" />
    </div>

    <LazySection
      name="trend"
      :active="overviewLoaded"
      :ready="isSectionVisible('trend')"
      placeholder="Trend charts will load when this section enters the viewport."
      @visible="loadSection"
    >
      <TrendSection
        :sales-vs-purchases="salesVsPurchases"
        :profit-trend="profitTrend"
        :profit-summary="profitSummary"
        :purchases-month="Number(kpi.purchases_month || 0)"
      />
    </LazySection>

    <LazySection
      name="financialTrends"
      :active="overviewLoaded"
      :ready="isSectionVisible('financialTrends')"
      placeholder="Financial trend charts will load when this section enters the viewport."
      @visible="loadSection"
    >
      <FinancialTrendsSection
        :financial-trend="financialTrend"
        :credit-payable-trend="creditPayableTrend"
        :credit-receivable-trend="creditReceivableTrend"
        :stock-value-trend="stockValueTrend"
      />
    </LazySection>

    <LazySection
      name="analytics"
      :active="overviewLoaded"
      :ready="isSectionVisible('analytics')"
      placeholder="Item analytics will load when you scroll here."
      @visible="loadSection"
    >
      <ItemAnalyticsSection
        :top-items-revenue="topItemsRevenue"
        :top-items-profit="topItemsProfit"
        :category-revenue="categoryRevenue"
      />
    </LazySection>

    <LazySection
      name="operations"
      :active="overviewLoaded"
      :ready="isSectionVisible('operations')"
      placeholder="Operational tables will load when this block reaches the viewport."
      @visible="loadSection"
    >
      <OperationsSection :slow-moving="slowMoving" :recent-bills="recentBills" />
    </LazySection>

    <LazySection
      name="inventory"
      :active="overviewLoaded"
      :ready="isSectionVisible('inventory')"
      placeholder="Inventory alerts and vendor insights will load lower down the page."
      @visible="loadSection"
    >
      <InventorySection :stock-alerts="stockAlerts" :top-vendors="topVendors" />
    </LazySection>
  </div>
</template>

<script>
import Swal from "sweetalert2";

import ChequeRegister from "./components/ChequeRegister.vue";
import FinancialTrendsSection from "./components/FinancialTrendsSection.vue";
import InventorySection from "./components/InventorySection.vue";
import ItemAnalyticsSection from "./components/ItemAnalyticsSection.vue";
import LazySection from "./components/LazySection.vue";
import MetricCard from "./components/MetricCard.vue";
import OperationsSection from "./components/OperationsSection.vue";
import PerformancePanel from "./components/PerformancePanel.vue";
import TrendSection from "./components/TrendSection.vue";
import { buildFinancialPositionCards, buildKpiCards } from "./dashboardCards";
import {
  getDashboardOverview,
  getFinancialTrendsSection,
  getInventorySection,
  getItemAnalyticsSection,
  getOperationsSection,
  getTrendSection,
} from "@/services/dashboard";

// Data loader for each lazily loaded section (keys match the LazySection names).
const SECTION_LOADERS = {
  trend: getTrendSection,
  financialTrends: getFinancialTrendsSection,
  analytics: getItemAnalyticsSection,
  operations: getOperationsSection,
  inventory: getInventorySection,
};

const sectionFlags = () => Object.fromEntries(Object.keys(SECTION_LOADERS).map((name) => [name, false]));

// Business dashboard page: owns the dashboard data and loading; the components only display it.
export default {
  name: "indexpage",
  components: {
    ChequeRegister,
    FinancialTrendsSection,
    InventorySection,
    ItemAnalyticsSection,
    LazySection,
    MetricCard,
    OperationsSection,
    PerformancePanel,
    TrendSection,
  },
  data() {
    return {
      loading: false,
      overviewLoaded: false,
      // sectionState: data loaded; sectionLoading: request in flight
      sectionState: sectionFlags(),
      sectionLoading: sectionFlags(),

      // Overview (loaded on page open)
      kpi: {
        revenue_today: 0,
        revenue_yesterday: 0,
        revenue_change_percent: 0,
        bills_today: 0,
        avg_bill_today: 0,
        cost_today: 0,
        profit_today: 0,
        margin_today_percent: 0,
        purchase_today: 0,
        purchase_yesterday: 0,
        purchase_change_percent: 0,
        purchases_month: 0,
        stock_value: 0,
        low_stock_count: 0,
        out_of_stock_count: 0,
        net_receivable: 0,
        receivable_parties: 0,
        net_payable: 0,
        payable_parties: 0,
      },
      profitSummary: {
        revenue_month: 0,
        cost_month: 0,
        gross_profit_month: 0,
        gross_margin_percent: 0,
      },
      financialPosition: {
        stock_value: 0,
        stock_value_change_percent: 0,
        credit_payable: 0,
        credit_payable_change_percent: 0,
        credit_receivable: 0,
        credit_receivable_change_percent: 0,
        bank_balance: 0,
        bank_balance_change_percent: 0,
        cash_in_shop: 0,
        cash_in_shop_change_percent: 0,
        net_asset: 0,
        net_asset_change_percent: 0,
      },
      performanceSummary: {
        business_today: 0,
        business_month: 0,
        profit_today: 0,
        profit_month: 0,
        expense_today: 0,
        expense_month: 0,
        credits_today: 0,
        credits_month: 0,
      },
      latestBankBalance: null,
      cheques: [],

      // Lazily loaded sections
      salesVsPurchases: { months: [], sales: [], purchases: [] },
      profitTrend: { months: [], revenue: [], cost: [], gross_profit: [] },
      financialTrend: { months: [], business: [], profit: [], expense: [], net_asset: [] },
      creditPayableTrend: { months: [], values: [] },
      creditReceivableTrend: { months: [], values: [] },
      stockValueTrend: { months: [], values: [] },
      topItemsRevenue: [],
      topItemsProfit: [],
      categoryRevenue: [],
      slowMoving: [],
      recentBills: [],
      stockAlerts: { low_stock_count: 0, out_of_stock_count: 0, low_stock_items: [], out_of_stock_items: [] },
      topVendors: [],
    };
  },
  computed: {
    hasData() {
      return this.salesVsPurchases.months.length || this.recentBills.length || this.topItemsRevenue.length;
    },
    kpiCards() {
      return buildKpiCards(this.kpi);
    },
    financialPositionCards() {
      return buildFinancialPositionCards(this.financialPosition);
    },
  },
  async mounted() {
    await this.loadOverview();
    this.overviewLoaded = true;
  },
  methods: {
    isSectionVisible(section) {
      return this.sectionState[section] || this.sectionLoading[section];
    },
    // Copy loaded values into the page state; undefined means "keep the current value".
    applyData(data) {
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined) this[key] = value;
      });
    },
    showLoadError(title, error) {
      Swal.fire({ icon: "error", title, text: error.response?.data?.detail || error.message });
    },
    async loadOverview() {
      this.loading = true;
      try {
        this.applyData(await getDashboardOverview());
      } catch (error) {
        this.showLoadError("Failed to load dashboard overview", error);
      } finally {
        this.loading = false;
      }
    },
    async loadSection(section, force = false) {
      if (this.sectionLoading[section] || (!force && this.sectionState[section])) {
        return;
      }
      this.sectionLoading[section] = true;
      try {
        this.applyData(await SECTION_LOADERS[section]());
        this.sectionState[section] = true;
      } catch (error) {
        this.showLoadError(`Failed to load ${section} section`, error);
      } finally {
        this.sectionLoading[section] = false;
      }
    },
    async refreshDashboard() {
      await this.loadOverview();
      const loadedSections = Object.keys(this.sectionState).filter((section) => this.sectionState[section]);
      await Promise.all(loadedSections.map((section) => this.loadSection(section, true)));
    },
  },
};
</script>

<style src="./dashboard.css"></style>
